<template>
  <div
    v-if="!disabled"
    ref="target"
    role="none"
    class="w-tooltip-target"
    :class="targetClasses"
    @mouseover="showTooltip"
    @mouseleave="hideTooltip"
    @focusin="showTooltip"
    @focusout="hideTooltip"
  >
    <slot />

    <teleport v-if="tooltipVisible" :to="tooltipVisible ? 'body' : undefined">
      <div ref="tooltipElement" class="w-tooltip-wrapper" :class="orientationClass" :style="tooltipPosition">
        <div class="w-tooltip" :class="componentClasses">
          <slot name="tooltip">
            <div v-html="translate(text)" />
          </slot>
        </div>
      </div>
    </teleport>
  </div>

  <slot v-else />
</template>

<script lang="ts">
import { defineComponent, ref, computed, nextTick } from 'vue';

import { breakpoint } from '../../composables/breakpoints';
import { usePosition } from '../../composables/position';
import { withTooltipProps, useTooltipProps } from '../../composables/props/tooltip';
import { useTranslation } from '../../composables/translation';

/**
 * Tooltip uniwersalny domyślnie umieszczany od góry
 */
export default defineComponent({
  props: {
    ...withTooltipProps
  },

  setup(props) {
    const componentName = 'w-tooltip';

    const { target, position, verticalPosition, orientationClass, setPosition } = usePosition({
      targetMargin: 180,
      orientationClassPrefix: 'w-tooltip--'
    });

    /**
     * Element HTML tooltipa
     */
    const tooltipElement = ref();

    /**
     * Czy tooltip jest widoczny
     */
    const tooltipVisible = ref(false);

    /**
     * Czy kursor jest nakierowany na target / lub jest focus na target
     */
    const targetIsHover = ref(false);

    /**
     * Pokazuje tooltip z wykorzystaniem skonfigurowanego opóźnienia
     */
    function showTooltip() {
      targetIsHover.value = true;
      if (!props.delay) {
        return setTootlipVisible();
      }

      setTimeout(() => {
        if (targetIsHover.value === true) {
          setTootlipVisible();
        }
      }, props.delay * 1000);
    }

    /**
     * Wyświetla tooltip
     */
    function setTootlipVisible(): void {
      setPosition();
      tooltipVisible.value = true;

      nextTick(() => {
        // Patrzy na koniec animacji modala
        tooltipElement.value?.addEventListener('animationend', removeClosingAttr);
      });
    }

    /**
     * Ukrywa tooltip
     */
    function hideTooltip() {
      targetIsHover.value = false;

      addClosingAttr();
    }

    /**
     * Zwraca odpowiednią pozycję tooltipa zależnie od rozdzielczości
     */
    const tooltipPosition = computed(() => {
      if (breakpoint.value.sm) {
        return verticalPosition.value;
      }
      return position.value;
    });

    /** Usuwa atrybut odpowiadający za animację zamykania  */
    function removeClosingAttr() {
      tooltipElement.value.removeAttribute('closing');

      if (!targetIsHover.value) {
        tooltipVisible.value = false;
      }
    }

    /** Dodaje atrybut odpowiadający za animację zamykania  */
    function addClosingAttr() {
      nextTick(() => {
        tooltipElement.value?.setAttribute('closing', '');
      });
    }

    return {
      target,
      tooltipElement,
      position,
      tooltipVisible,
      tooltipPosition,
      orientationClass,
      breakpoint,
      showTooltip,
      hideTooltip,
      ...useTranslation(),
      ...useTooltipProps(componentName, props)
    };
  }
});
</script>
<style lang="scss">
@use 'WTooltip';
</style>
